// Exhaustive switch over TimelineEntry kinds. Adding a new TimelineEntry
// variant breaks the `never` check at the bottom — the type-checker forces
// the renderer to handle it.

import type { TimelineEntry } from "../useWsSession";

import { buildReadinessAcceptMessage } from "./acceptMessage";
import { AssistantTurn } from "./AssistantTurn";
import { CodingProgress } from "./CodingProgress";
import { CreateIssueProposal } from "./CreateIssueProposal";
import { ErrorToast } from "./ErrorToast";
import { LifecycleNotice } from "./LifecycleNotice";
import { ProposalCard } from "./ProposalCard";
import { ReadinessCard } from "./ReadinessCard";
import { RoleSpawnBoundary } from "./RoleSpawnBoundary";
import { ToolCallCard } from "./ToolCallCard";
import { UserMessage } from "./UserMessage";
import { BudgetExhausted } from "./BudgetExhausted";
import { ToolsUnavailable } from "./ToolsUnavailable";

// A tool result renders as a ToolCallCard and nothing else. Two confirm
// controls used to hang off one here and BOTH were dead ends:
//
//   - `groom_backlog`'s patch list flipped a client-side latch to
//     "Applied N patches." with nothing leaving the browser — `onGroomConfirm`
//     was never passed, `applyProposal` has no groom kind, and the tool is
//     deregistered now.
//   - `propose_links`' selector sent "Apply these link candidates: #42
//     (relates_to)." as an ordinary user message that no tool can act on.
//     `propose_links` itself stays: it answers "what relates to #42" in prose
//     here, and the ISSUE EDITOR wires it correctly (`dashboard/propose.ts` →
//     `useProposeLinks`, accepted links written at create by `runIntakeApply`).
//
// Neither gate could ever have fired on a real transcript anyway: both keyed
// on the BARE workflow id while the servant emits `loom__<id>` and the sandbox
// `mcp__loom__<id>`. A button that does nothing must not survive.
interface RenderEntryProps {
  readonly entry: TimelineEntry;
  /** Phase 10a — confirm a `proposal_pending` entry. */
  readonly onProposalConfirm?: (proposalId: string) => void;
  /** Phase 10a — reject a `proposal_pending` entry. */
  readonly onProposalReject?: (proposalId: string) => void;
  /**
   * Called when the user clicks an "Accept changes" button inside an
   * assistant message (rendered from a `loom-confirm` fenced block).
   * Wired to `useWsSession.send` from ChatPage; sends an affirmative
   * reply that the agent treats as confirmation. Also drives readiness-card
   * answer widgets (each sends a canonical user message).
   */
  readonly onAcceptChanges?: (text: string) => void;
  /** Project context for the readiness card's milestone dropdown. */
  readonly projectId?: number;
  /** The project a green milestone row is checked against when there is no
   *  dropdown (`ReadinessCard.milestoneListProjectId`). */
  readonly milestoneListProjectId?: number;
  /** The latest FAILED apply, when there is one. The card renders it and
   *  disarms Accept over a spent claim; without it a refused apply is invisible
   *  and the card keeps saying "Ready". */
  readonly applyFailure?: { readonly issueIid: number; readonly error: string };
  /**
   * The chat's HELD answers for its inline readiness cards (see ChatPage): a
   * click on Loom's answer, or a typed Send, is held while the agent works and
   * sent when it goes idle, instead of killing the running turn. Omitted ⇒ a
   * card row answers straight through `onAcceptChanges`.
   */
  readonly cardHold?: {
    readonly onAnswer: (issueIid: number, field: string, text: string) => void;
    readonly onUndo: (issueIid: number, field: string) => void;
    readonly held: (issueIid: number) => ReadonlyMap<string, string>;
    readonly sent: (issueIid: number) => ReadonlySet<string>;
    readonly settled: (issueIid: number, field: string) => boolean;
  };
}

const noop = () => undefined;

const assertExhaustive = (_x: never): never => {
  throw new Error("non-exhaustive TimelineEntry switch");
};

export function RenderEntry({
  entry,
  onProposalConfirm,
  onProposalReject,
  onAcceptChanges,
  projectId,
  milestoneListProjectId,
  applyFailure,
  cardHold,
}: RenderEntryProps) {
  switch (entry.kind) {
    case "assistant_turn":
      return (
        <AssistantTurn
          text={entry.text}
          entryId={entry.id}
          {...(onAcceptChanges ? { onAcceptChanges } : {})}
        />
      );
    case "user_message":
      return <UserMessage text={entry.text} />;
    case "tool_call":
      return (
        <ToolCallCard
          toolUseId={entry.toolUseId}
          {...(entry.toolName !== undefined ? { toolName: entry.toolName } : {})}
          {...(entry.input !== undefined ? { input: entry.input } : {})}
          {...(entry.output !== undefined ? { output: entry.output } : {})}
          {...(entry.ok !== undefined ? { ok: entry.ok } : {})}
        />
      );
    case "lifecycle":
      return (
        <LifecycleNotice handler={entry.handler} phase={entry.phase} outcome={entry.outcome} />
      );
    case "role":
      return (
        <RoleSpawnBoundary
          role={entry.role}
          task={entry.task}
          stateKey={`role:${entry.toolUseId}`}
          {...(entry.summary !== undefined ? { summary: entry.summary } : {})}
        />
      );
    case "error":
      return <ErrorToast message={entry.message} terminal={entry.terminal} />;
    case "coding_progress":
      return <CodingProgress entry={entry.entry} />;
    case "budget_exhausted":
      return <BudgetExhausted budget={entry.budget} limit={entry.limit} used={entry.used} />;
    case "tools_unavailable":
      return <ToolsUnavailable />;
    case "proposal":
      if (entry.proposal.kind === "create_issue_with_link_proposals") {
        return (
          <CreateIssueProposal
            proposalId={entry.proposalId}
            proposal={entry.proposal}
            state={entry.state}
            onConfirm={(id) => (onProposalConfirm ?? noop)(id)}
            onReject={onProposalReject ?? noop}
          />
        );
      }
      return (
        <ProposalCard
          proposalId={entry.proposalId}
          proposal={entry.proposal}
          state={entry.state}
          onConfirm={onProposalConfirm ?? noop}
          onReject={onProposalReject ?? noop}
        />
      );
    case "breakdown_card":
      // Rendered PINNED by the flow surfaces (IssuePage / RefineFlow) via
      // useBreakdownCard — never inline in the scrolling chat, where a second
      // copy would double-render and its Confirm would race the pinned one.
      return null;
    case "agent_ask":
      // Typed pre-card asks are PINNED as a step by the Focus refine surface,
      // which owns the answer widget and the attribution the structured
      // `question_answer` frame needs. An inline copy here would offer a second,
      // unattributed way to answer the same question — the exact failure the
      // channel exists to remove.
      return null;
    case "readiness_card":
      return (
        <ReadinessCard
          issueIid={entry.issueIid}
          verdicts={entry.verdicts}
          dor={entry.dor}
          dod={entry.dod}
          invest={entry.invest}
          methodologySkipped={entry.methodologySkipped}
          allGreen={entry.allGreen}
          {...(entry.acceptToken !== undefined
            ? { acceptToken: entry.acceptToken }
            : {})}
          {...(projectId !== undefined ? { projectId } : {})}
          {...(milestoneListProjectId !== undefined ? { milestoneListProjectId } : {})}
          {...(applyFailure !== undefined &&
          applyFailure.issueIid === entry.issueIid
            ? { applyFailure: { error: applyFailure.error } }
            : {})}
          {...(cardHold !== undefined
            ? {
                onAnswer: (text: string, field: string) =>
                  cardHold.onAnswer(entry.issueIid, field, text),
                onUndo: (field: string) => cardHold.onUndo(entry.issueIid, field),
                held: cardHold.held(entry.issueIid),
                sent: cardHold.sent(entry.issueIid),
                settled: (field: string) => cardHold.settled(entry.issueIid, field),
                traceSurface: "chat_card",
              }
            : onAcceptChanges
              ? // The text alone: this lane is a plain chat message.
                { onAnswer: (text: string) => onAcceptChanges(text) }
              : {})}
          onAccept={() =>
            (onAcceptChanges ?? noop)(
              buildReadinessAcceptMessage(entry.issueIid, entry.acceptToken),
            )
          }
        />
      );
    default:
      return assertExhaustive(entry);
  }
}
